// src/components/actes/ActeDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  doc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  addDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadMultipleFiles, deleteFile } from '../../services/uploadService';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Edit,
  Trash2,
  Save,
  Calendar,
  Clock,
  User,
  FileText,
  Euro,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Repeat,
  Upload,
  Camera,
  Printer,
  Check
} from 'lucide-react';

const ActeDetailModal = ({ isOpen, onClose, acteId, onUpdate, onDelete }) => {
  const [acte, setActe] = useState(null);
  const [animal, setAnimal] = useState(null);
  const [intervenant, setIntervenant] = useState(null);
  const [typeActe, setTypeActe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedActe, setEditedActe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [intervenants, setIntervenants] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [newReportDate, setNewReportDate] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  // Fonction pour afficher un toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2000);
  };

  // Composant Toast
  const Toast = () => {
    if (!toast) return null;
    
    return (
      <div className="fixed top-4 right-4 z-[60] animate-pulse">
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white`}>
          {toast.type === 'success' ? (
            <Check className="h-5 w-5" />
          ) : (
            <X className="h-5 w-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isOpen && acteId) {
      loadActeDetails();
    }
  }, [isOpen, acteId]);

  const loadActeDetails = async () => {
    try {
      setLoading(true);
      
      // Charger l'acte
      const acteDoc = await getDoc(doc(db, 'actes', acteId));
      if (!acteDoc.exists()) {
        console.error('Acte non trouvé');
        onClose();
        return;
      }
      
      const acteData = { id: acteDoc.id, ...acteDoc.data() };
      setActe(acteData);
      setEditedActe(acteData);
      
      // Charger l'animal
      if (acteData.animalId) {
        const animalDoc = await getDoc(doc(db, 'animaux', acteData.animalId));
        if (animalDoc.exists()) {
          setAnimal({ id: animalDoc.id, ...animalDoc.data() });
        }
      }
      
      // Charger l'intervenant
      if (acteData.intervenantId) {
        const intervenantDoc = await getDoc(doc(db, 'intervenants', acteData.intervenantId));
        if (intervenantDoc.exists()) {
          setIntervenant({ id: intervenantDoc.id, ...intervenantDoc.data() });
        }
      }
      
      // Charger le type d'acte pour les champs spécifiques et la couleur
      if (acteData.typeActeId) {
        const typeActeDoc = await getDoc(doc(db, 'typesActes', acteData.typeActeId));
        if (typeActeDoc.exists()) {
          setTypeActe({ id: typeActeDoc.id, ...typeActeDoc.data() });
        }
      } else {
        // Si pas de typeActeId, essayer de trouver par typeCategorie et nomActe
        const typesActesQuery = query(
          collection(db, 'typesActes'),
          where('typeCategorie', '==', acteData.typeCategorie),
          where('nomActe', '==', acteData.nomActe)
        );
        const typesActesSnap = await getDocs(typesActesQuery);
        if (!typesActesSnap.empty) {
          const typeActeData = typesActesSnap.docs[0];
          setTypeActe({ id: typeActeData.id, ...typeActeData.data() });
        }
      }
      
      // Charger la liste des intervenants pour l'édition
      const intervenantsSnap = await getDocs(
        query(collection(db, 'intervenants'), where('actif', '==', true))
      );
      const intervenantsData = intervenantsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setIntervenants(intervenantsData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedActe(acte);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const updatedData = {
        ...editedActe,
        updatedAt: new Date().toISOString(),
        historique: [
          ...(editedActe.historique || []),
          {
            date: new Date().toISOString(),
            action: 'modification',
            details: 'Acte modifié'
          }
        ]
      };
      
      // Si l'intervenant a changé, mettre à jour le nom
      if (editedActe.intervenantId !== acte.intervenantId) {
        const newIntervenant = intervenants.find(i => i.id === editedActe.intervenantId);
        updatedData.intervenantNom = newIntervenant ? 
          `${newIntervenant.prenom} ${newIntervenant.nom}` : null;
      }
      
      await updateDoc(doc(db, 'actes', acteId), updatedData);
      
      // Vérifier si la récurrence est active et si le prochain acte existe
      if (editedActe.recurrence?.active && acte.statut === 'realise') {
        // Vérifier si le prochain acte existe et n'est pas supprimé
        if (acte.prochaineEcheanceId) {
          const nextActeDoc = await getDoc(doc(db, 'actes', acte.prochaineEcheanceId));
          if (!nextActeDoc.exists() || nextActeDoc.data().statut === 'supprime') {
            // Le prochain acte n'existe pas ou est supprimé, on le recrée
            await createNextRecurringActe();
          }
        } else if (!acte.prochaineEcheanceCreee) {
          // Pas de prochain acte créé, on le crée
          await createNextRecurringActe();
        }
      }
      
      setActe(updatedData);
      setIsEditing(false);
      
      if (onUpdate) {
        onUpdate(updatedData);
      }
      
      showToast('Acte modifié');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      // Soft delete - on marque comme supprimé au lieu de supprimer physiquement
      const updatedData = {
        statut: 'supprime',
        dateSupression: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        historique: [
          ...(acte.historique || []),
          {
            date: new Date().toISOString(),
            action: 'suppression',
            details: 'Acte supprimé'
          }
        ]
      };
      
      await updateDoc(doc(db, 'actes', acteId), updatedData);
      
      // Note: On garde les documents associés car c'est un soft delete
      
      if (onDelete) {
        onDelete(acteId);
      }
      
      showToast('Acte supprimé');
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showToast('Erreur lors de la suppression', 'error');
      setShowDeleteConfirm(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Si on veut reporter, ouvrir le modal de report
    if (newStatus === 'reporte') {
      setNewReportDate(acte.dateActe);
      setShowReportModal(true);
      return;
    }

    try {
      const updatedData = {
        statut: newStatus,
        dateRealisation: newStatus === 'realise' ? new Date().toISOString() : null,
        updatedAt: new Date().toISOString(),
        historique: [
          ...(acte.historique || []),
          {
            date: new Date().toISOString(),
            action: 'changement_statut',
            details: `Statut changé en ${getStatutLabel(newStatus)}`
          }
        ]
      };
      
      await updateDoc(doc(db, 'actes', acteId), updatedData);
      
      // Si l'acte est marqué comme réalisé ET qu'il a une récurrence active
      if (newStatus === 'realise' && acte.recurrence?.active) {
        await createNextRecurringActe();
      }
      
      setActe({ ...acte, ...updatedData });
      setEditedActe({ ...editedActe, ...updatedData });
      
      if (onUpdate) {
        onUpdate({ ...acte, ...updatedData });
      }
      
      showToast(`Statut changé : ${getStatutLabel(newStatus)}`);
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showToast('Erreur lors du changement de statut', 'error');
    }
  };

  const handleReport = async () => {
    if (!newReportDate) {
      showToast('Veuillez sélectionner une nouvelle date', 'error');
      return;
    }

    try {
      const updatedData = {
        dateActe: newReportDate,
        statut: 'planifie', // Remettre en planifié
        updatedAt: new Date().toISOString(),
        historique: [
          ...(acte.historique || []),
          {
            date: new Date().toISOString(),
            action: 'report',
            details: `Acte reporté du ${new Date(acte.dateActe).toLocaleDateString('fr-FR')} au ${new Date(newReportDate).toLocaleDateString('fr-FR')}`
          }
        ]
      };
      
      await updateDoc(doc(db, 'actes', acteId), updatedData);
      
      setActe({ ...acte, ...updatedData });
      setEditedActe({ ...editedActe, ...updatedData });
      
      if (onUpdate) {
        onUpdate({ ...acte, ...updatedData });
      }
      
      setShowReportModal(false);
      showToast(`Acte reporté au ${new Date(newReportDate).toLocaleDateString('fr-FR')}`);
    } catch (error) {
      console.error('Erreur lors du report:', error);
      showToast('Erreur lors du report', 'error');
    }
  };

  const calculateNextDate = (currentDate, recurrenceType) => {
    const date = new Date(currentDate);
    
    // Gérer les deux formats possibles
    const type = recurrenceType === 'mensuelle' ? 'mensuel' : recurrenceType;
    
    switch (type) {
      case 'hebdomadaire':
        date.setDate(date.getDate() + 7);
        break;
      case 'mensuel':
      case 'mensuelle':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'trimestriel':
      case 'trimestrielle':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'semestriel':
      case 'semestrielle':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'annuel':
      case 'annuelle':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        // Si type personnalisé avec intervalle en jours
        if (acte.recurrence?.intervalleJours) {
          date.setDate(date.getDate() + acte.recurrence.intervalleJours);
        } else {
          // Par défaut, mensuel
          date.setMonth(date.getMonth() + 1);
        }
        break;
    }
    
    // Formater la date en YYYY-MM-DD sans UTC
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const createNextRecurringActe = async () => {
    if (!acte.recurrence?.active) {
      return;
    }
    
    try {
      // Calculer la prochaine date
      const nextDate = calculateNextDate(acte.dateActe, acte.recurrence.typeRecurrence);
      


      // Créer le nouvel acte avec toutes les informations nécessaires
      const newActeData = {
  nomActe: acte.nomActe,
  typeCategorie: acte.typeCategorie,
  typeActeId: acte.typeActeId || null,
  animalId: acte.animalId,
  dateActe: nextDate,
  heureActe: acte.heureActe || '', // Heure optionnelle
  statut: 'planifie',
  dateRealisation: null,
  intervenantId: acte.intervenantId || null,
  intervenantNom: acte.intervenantNom || null,
  cout: acte.cout || null,
  description: acte.description || '',
  notes: acte.notes || '',
  couleur: acte.couleur || null,
  champsSpecifiques: acte.champsSpecifiques || {},
  // Utiliser le rappel configuré pour les récurrences (pas celui de l'acte actuel)
  rappel: acte.recurrence?.rappelRecurrent || null,
  // AJOUT - Conserver le rappel de l'acte parent
  rappel: acte.rappel || null,
  recurrence: acte.recurrence, // Conserver les mêmes paramètres de récurrence
  documents: [], // Pas de documents pour le nouvel acte
  historique: [
    {
      date: new Date().toISOString(),
      action: 'création',
      details: `Acte créé automatiquement par récurrence de l'acte du ${new Date(acte.dateActe).toLocaleDateString('fr-FR')}`
    }
  ],
  acteParentId: acte.id, // Référence à l'acte parent
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};


      
      // Ajouter le nouvel acte à Firebase
      const docRef = await addDoc(collection(db, 'actes'), newActeData);
      
      // Mettre à jour l'acte actuel pour indiquer qu'un acte suivant a été créé
      await updateDoc(doc(db, 'actes', acteId), {
        prochaineEcheanceCreee: true,
        prochaineEcheanceId: docRef.id,
        prochaineEcheanceDate: nextDate,
        updatedAt: new Date().toISOString()
      });
      
      // Recharger les données si onUpdate est disponible
      if (onUpdate) {
        onUpdate();
      }
      
      showToast(`Prochain acte créé : ${new Date(nextDate).toLocaleDateString('fr-FR')}`);
    } catch (error) {
      console.error('Erreur lors de la création de l\'acte récurrent:', error);
      showToast('Erreur lors de la création de l\'acte récurrent', 'error');
    }
  };

  const handleFileUpload = async (files) => {
    setUploadingFiles(true);
    
    try {
      const result = await uploadMultipleFiles(
        files, 
        'actes',
        (progress) => {
          console.log(`Upload: ${progress.current}/${progress.total} - ${progress.percentage}%`);
        }
      );
      
      if (result.success.length > 0) {
        const updatedDocuments = [...(editedActe.documents || []), ...result.success];
        setEditedActe({ ...editedActe, documents: updatedDocuments });
        
        // Sauvegarder automatiquement si pas en mode édition
        if (!isEditing) {
          await updateDoc(doc(db, 'actes', acteId), {
            documents: updatedDocuments,
            updatedAt: new Date().toISOString()
          });
          setActe({ ...acte, documents: updatedDocuments });
        }
      }
      
      if (result.errors.length > 0) {
        result.errors.forEach(err => {
          showToast(`Erreur upload ${err.fileName}`, 'error');
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      showToast('Erreur lors de l\'upload', 'error');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    const docToDelete = editedActe.documents.find(d => d.id === docId);
    if (docToDelete) {
      try {
        await deleteFile(docToDelete);
      } catch (error) {
        console.error('Erreur lors de la suppression du fichier:', error);
      }
    }
    
    const updatedDocuments = editedActe.documents.filter(d => d.id !== docId);
    setEditedActe({ ...editedActe, documents: updatedDocuments });
    
    if (!isEditing) {
      await updateDoc(doc(db, 'actes', acteId), {
        documents: updatedDocuments,
        updatedAt: new Date().toISOString()
      });
      setActe({ ...acte, documents: updatedDocuments });
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'realise': return 'text-green-600 bg-green-100';
      case 'planifie': return 'text-gray-600 bg-gray-100';
      case 'annule': return 'text-red-600 bg-red-100';
      case 'reporte': return 'text-orange-600 bg-orange-100';
      case 'supprime': return 'text-gray-400 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'realise': return CheckCircle;
      case 'planifie': return Calendar;
      case 'annule': return XCircle;
      case 'reporte': return Clock;
      case 'supprime': return Trash2;
      default: return AlertCircle;
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'realise': return 'Réalisé';
      case 'planifie': return 'Planifié';
      case 'annule': return 'Annulé';
      case 'reporte': return 'Reporté';
      case 'supprime': return 'Supprimé';
      default: return statut;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const goToAnimalPage = () => {
    if (animal?.id) {
      onClose();
      navigate(`/animaux/${animal.id}`);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!acte) return null;

  const StatusIcon = getStatutIcon(acte.statut);
  const couleurActe = typeActe?.couleur || acte.couleur || '#6b7280';

  return (
    <>
      <Toast />
      
      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[55] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Supprimer l'acte
                </h3>
                <div className="mt-2 text-sm text-gray-600">
                  <p>Êtes-vous sûr de vouloir supprimer cet acte ?</p>
                  <p className="mt-1">
                    <span className="font-medium">{acte.typeCategorie} - {acte.nomActe}</span>
                    {animal && <span> pour {animal.nom}</span>}
                  </p>
                  
                  {/* Avertissement si récurrence active */}
                  {acte.recurrence?.active && (
                    <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-sm text-orange-800 font-medium flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        Attention : Acte récurrent
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        Cet acte fait partie d'une série récurrente. Pour arrêter la récurrence, 
                        désactivez d'abord la récurrence en modifiant l'acte.
                      </p>
                    </div>
                  )}
                  
                  {/* Avertissement si c'est un acte créé par récurrence */}
                  {acte.acteParentId && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm text-yellow-800 font-medium flex items-center gap-1">
                        <Repeat className="h-4 w-4" />
                        Acte créé automatiquement
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Cet acte a été créé automatiquement par récurrence. 
                        Sa suppression n'empêchera pas la création du prochain acte récurrent.
                      </p>
                    </div>
                  )}
                  
                  <p className="mt-2 text-xs text-gray-500">
                    L'acte sera masqué de la vue normale mais restera consultable dans les actes supprimés.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={acte.recurrence?.active}
                className={`px-4 py-2 rounded-lg font-medium ${
                  acte.recurrence?.active 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {acte.recurrence?.active ? 'Suppression impossible' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de report */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[55] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reporter l'acte</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouvelle date
              </label>
              <input
                type="date"
                value={newReportDate}
                onChange={(e) => setNewReportDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReport}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Reporter
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* En-tête */}
        <div className="border-b">
          {/* Titre avec fond coloré */}
          <div 
            className="px-6 pt-6 pb-3"
            style={{
              backgroundColor: couleurActe + '15',
              borderTop: `4px solid ${couleurActe}`
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">
                  {acte.typeCategorie} - <span className="font-bold">{acte.nomActe}</span>
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatutColor(acte.statut)}`}>
                  <StatusIcon className="inline h-4 w-4 mr-1" />
                  {getStatutLabel(acte.statut)}
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 hover:bg-opacity-50 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Actions rapides */}
          <div className="px-6 py-3 bg-white flex gap-2">
            {!isEditing && acte.statut === 'planifie' && (
              <>
                <button
                  onClick={() => handleStatusChange('realise')}
                  className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Marquer comme réalisé
                </button>
                <button
                  onClick={() => handleStatusChange('reporte')}
                  className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Reporter
                </button>
                <button
                  onClick={() => handleStatusChange('annule')}
                  className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Marquer comme annulé
                </button>
              </>
            )}
            
            {!isEditing && acte.statut === 'realise' && (
              <button
                onClick={() => handleStatusChange('planifie')}
                className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
              >
                Repasser en planifié
              </button>
            )}
            
            {!isEditing && acte.statut === 'annule' && (
              <button
                onClick={() => handleStatusChange('planifie')}
                className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
              >
                Reprogrammer
              </button>
            )}
            
            {!isEditing && acte.statut === 'supprime' && (
              <div className="px-3 py-1 text-sm text-gray-500">
                Cet acte a été supprimé
              </div>
            )}
            
            <div className="flex-1" />
            
            {!isEditing ? (
              <>
                <button
                  onClick={goToAnimalPage}
                  className="px-3 py-1 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Fiche Cheval
                </button>
                <button
                  onClick={handlePrint}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Imprimer"
                >
                  <Printer className="h-4 w-4" />
                </button>
                {acte.statut !== 'supprime' && (
                  <>
                    <button
                      onClick={handleEdit}
                      className="px-3 py-1 text-sm border border-black text-black rounded-lg hover:bg-gray-50"
                    >
                      <Edit className="inline h-4 w-4 mr-1" />
                      Modifier
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="inline h-4 w-4 mr-1" />
                      Supprimer
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`px-3 py-1 text-sm rounded-lg ${
                    saving 
                      ? 'bg-gray-300 text-gray-500' 
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  <Save className="inline h-4 w-4 mr-1" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Informations principales */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Informations générales</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {/* Animal */}
                  <div className="flex items-center gap-3">
                    {animal?.photoUrl && (
                      <img
                        src={animal.photoUrl}
                        alt={animal.nom}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                   <div>
  <div className="text-sm text-gray-500">Animal</div>
  <div className="font-medium">{animal?.nom}</div>
  <div className="text-sm text-gray-600">
    {animal?.sire || '-'} • {
      animal?.dateNaissance ? 
        (() => {
          const birthDate = new Date(animal.dateNaissance);
          const today = new Date();
          
          let years = today.getFullYear() - birthDate.getFullYear();
          let months = today.getMonth() - birthDate.getMonth();
          
          if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
            years--;
            months += 12;
          }
          
          if (years === 0) {
            // Moins d'un an
            return months === 1 ? '1 mois' : `${months} mois`;
          } else if (years === 1 && months === 0) {
            // Exactement 1 an
            return '1 an';
          } else if (years === 1) {
            // 1 an et quelques mois
            return `1 an ${months} mois`;
          } else {
            // Plus d'un an (sans les mois pour simplifier)
            return `${years} ans`;
          }
        })() : 
        '-'
    }
  </div>
</div>
                  </div>
                  
                  {/* Date et heure */}
                  <div>
                    <div className="text-sm text-gray-500">Date et heure</div>
                    {isEditing ? (
                      <div className="flex gap-2 mt-1">
                        <input
                          type="date"
                          value={editedActe.dateActe}
                          onChange={(e) => setEditedActe({ ...editedActe, dateActe: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded"
                        />
                        <input
                          type="time"
                          value={editedActe.heureActe}
                          onChange={(e) => setEditedActe({ ...editedActe, heureActe: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded"
                        />
                      </div>
                    ) : (
                     <div className="font-medium">
  {new Date(acte.dateActe).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })}
  {acte.heureActe && ` à ${acte.heureActe}`}
</div>
                    )}
                  </div>
                  
                  {/* Intervenant */}
                  <div>
                    <div className="text-sm text-gray-500">Intervenant</div>
                    {isEditing ? (
                      <select
                        value={editedActe.intervenantId || ''}
                        onChange={(e) => setEditedActe({ ...editedActe, intervenantId: e.target.value })}
                        className="w-full mt-1 px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="">Aucun intervenant</option>
                        {intervenants.map(int => (
                          <option key={int.id} value={int.id}>
                            {int.prenom} {int.nom} - {int.type}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="font-medium">
                        {intervenant ? (
                          <>
                            {intervenant.prenom} {intervenant.nom}
                            <div className="text-sm text-gray-600">
                              {intervenant.type}
                              {intervenant.telephone && ` • ${intervenant.telephone}`}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400">Non renseigné</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Coût */}
                  <div>
                    <div className="text-sm text-gray-500">Coût</div>
                    {isEditing ? (
                      <div className="relative mt-1">
                        <Euro className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          value={editedActe.cout || ''}
                          onChange={(e) => setEditedActe({ ...editedActe, cout: e.target.value })}
                          className="w-full pl-8 pr-2 py-1 border border-gray-300 rounded"
                          placeholder="0.00"
                        />
                      </div>
                    ) : (
                      <div className="font-medium">
                        {acte.cout ? `${parseFloat(acte.cout).toFixed(2)} €` : 
                         <span className="text-gray-400">Non renseigné</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Champs spécifiques */}
              {typeActe?.champsSpecifiques && typeActe.champsSpecifiques.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Informations spécifiques</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {typeActe.champsSpecifiques.map(champ => {
                      const value = editedActe.champsSpecifiques?.[champ.nom];
                      
                      return (
                        <div key={champ.nom}>
                          <div className="text-sm text-gray-500">{champ.label}</div>
                          {isEditing ? (
                            <div className="mt-1">
                              {champ.type === 'text' && (
                                <input
                                  type="text"
                                  value={value || ''}
                                  onChange={(e) => setEditedActe({
                                    ...editedActe,
                                    champsSpecifiques: {
                                      ...editedActe.champsSpecifiques,
                                      [champ.nom]: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded"
                                />
                              )}
                              {champ.type === 'select' && (
                                <select
                                  value={value || ''}
                                  onChange={(e) => setEditedActe({
                                    ...editedActe,
                                    champsSpecifiques: {
                                      ...editedActe.champsSpecifiques,
                                      [champ.nom]: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded"
                                >
                                  <option value="">Sélectionner...</option>
                                  {champ.options?.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              )}
                              {champ.type === 'date' && (
                                <input
                                  type="date"
                                  value={value || ''}
                                  onChange={(e) => setEditedActe({
                                    ...editedActe,
                                    champsSpecifiques: {
                                      ...editedActe.champsSpecifiques,
                                      [champ.nom]: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded"
                                />
                              )}
                              {champ.type === 'number' && (
                                <input
                                  type="number"
                                  value={value || ''}
                                  onChange={(e) => setEditedActe({
                                    ...editedActe,
                                    champsSpecifiques: {
                                      ...editedActe.champsSpecifiques,
                                      [champ.nom]: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded"
                                />
                              )}
                              {champ.type === 'textarea' && (
                                <textarea
                                  value={value || ''}
                                  onChange={(e) => setEditedActe({
                                    ...editedActe,
                                    champsSpecifiques: {
                                      ...editedActe.champsSpecifiques,
                                      [champ.nom]: e.target.value
                                    }
                                  })}
                                  rows="2"
                                  className="w-full px-2 py-1 border border-gray-300 rounded"
                                />
                              )}
                            </div>
                          ) : (
                            <div className="font-medium">
                              {value || <span className="text-gray-400">Non renseigné</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Section Rappel - NOUVELLE SECTION */}
{(acte.statut === 'planifie' || (isEditing && editedActe.statut === 'planifie')) && (
  <div>
    <h3 className="font-medium text-gray-900 mb-2">
      <Clock className="inline h-4 w-4 mr-1" />
      Rappel
    </h3>
    <div className="bg-blue-50 rounded-lg p-4">
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editedActe.rappel?.active || false}
                onChange={(e) => setEditedActe({
                  ...editedActe,
                  rappel: {
                    active: e.target.checked,
                    joursAvant: editedActe.rappel?.joursAvant || 7
                  }
                })}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Activer un rappel pour cet acte</span>
            </label>
          </div>
          
          {editedActe.rappel?.active && (
            <div>
              <label className="text-sm text-gray-500">Rappel avant l'échéance (jours)</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  value={editedActe.rappel?.joursAvant || 7}
                  onChange={(e) => setEditedActe({
                    ...editedActe,
                    rappel: {
                      ...editedActe.rappel,
                      joursAvant: parseInt(e.target.value) || 7
                    }
                  })}
                  min="1"
                  max="30"
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">
                  jour{(editedActe.rappel?.joursAvant || 7) > 1 ? 's' : ''} avant
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Date du rappel : {
                  new Date(
                    new Date(editedActe.dateActe).getTime() - 
                    (editedActe.rappel?.joursAvant || 7) * 24 * 60 * 60 * 1000
                  ).toLocaleDateString('fr-FR')
                }
              </p>
            </div>
          )}
        </div>
      ) : (
        acte.rappel?.active ? (
          <div className="text-sm space-y-2">
            <div>
              <span className="text-gray-600">Statut:</span>{' '}
              <span className="font-medium text-blue-600">Actif</span>
            </div>
            <div>
              <span className="text-gray-600">Rappel:</span>{' '}
              <span className="font-medium">
                {acte.rappel.joursAvant} jour{acte.rappel.joursAvant > 1 ? 's' : ''} avant l'échéance
              </span>
            </div>
            <div>
              <span className="text-gray-600">Date du rappel:</span>{' '}
              <span className="font-medium">
                {new Date(
                  new Date(acte.dateActe).getTime() - 
                  acte.rappel.joursAvant * 24 * 60 * 60 * 1000
                ).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-400">Aucun rappel configuré</div>
        )
      )}
    </div>
  </div>
)}

{/* Récurrence */}
<div>
  <h3 className="font-medium text-gray-900 mb-2">
    <Repeat className="inline h-4 w-4 mr-1" />
    Récurrence
  </h3>
  <div className="bg-gray-50 rounded-lg p-4">
    {isEditing ? (
      <div className="space-y-3">
        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={editedActe.recurrence?.active || false}
              onChange={(e) => setEditedActe({
                ...editedActe,
                recurrence: {
                  ...editedActe.recurrence,
                  active: e.target.checked
                }
              })}
              className="rounded text-black focus:ring-gray-700"
            />
            <span className="text-sm font-medium">Activer la récurrence</span>
          </label>
        </div>
        
        {editedActe.recurrence?.active && (
          <>
            <div>
              <label className="text-sm text-gray-500">Type de récurrence</label>
              <select
                value={editedActe.recurrence?.typeRecurrence || 'mensuel'}
                onChange={(e) => setEditedActe({
                  ...editedActe,
                  recurrence: {
                    ...editedActe.recurrence,
                    typeRecurrence: e.target.value
                  }
                })}
                className="w-full mt-1 px-2 py-1 border border-gray-300 rounded"
              >
                <option value="hebdomadaire">Hebdomadaire</option>
                <option value="mensuel">Mensuel</option>
                <option value="trimestriel">Trimestriel (3 mois)</option>
                <option value="semestriel">Semestriel (6 mois)</option>
                <option value="annuel">Annuel</option>
              </select>
            </div>
            
            {/* NOUVEAU : Rappel pour les actes récurrents futurs */}
            <div className="bg-green-50 border border-green-200 rounded p-2">
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={editedActe.recurrence?.rappelRecurrent?.active || false}
                  onChange={(e) => setEditedActe({
                    ...editedActe,
                    recurrence: {
                      ...editedActe.recurrence,
                      rappelRecurrent: {
                        active: e.target.checked,
                        joursAvant: editedActe.recurrence?.rappelRecurrent?.joursAvant || 7
                      }
                    }
                  })}
                  className="rounded text-green-600 focus:ring-green-500"
                />
                <span className="text-xs font-medium text-green-800">
                  Rappel pour les prochains actes
                </span>
              </label>
              
              {editedActe.recurrence?.rappelRecurrent?.active && (
                <div className="ml-6">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={editedActe.recurrence?.rappelRecurrent?.joursAvant || 7}
                      onChange={(e) => setEditedActe({
                        ...editedActe,
                        recurrence: {
                          ...editedActe.recurrence,
                          rappelRecurrent: {
                            ...editedActe.recurrence.rappelRecurrent,
                            joursAvant: parseInt(e.target.value) || 7
                          }
                        }
                      })}
                      min="1"
                      max="30"
                      className="w-16 px-1 py-0.5 border border-green-300 rounded text-xs"
                    />
                    <span className="text-xs text-green-700">
                      jour{(editedActe.recurrence?.rappelRecurrent?.joursAvant || 7) > 1 ? 's' : ''} avant
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    ) : (
      acte.recurrence?.active ? (
        <div className="text-sm space-y-2">
          <div>
            <span className="text-gray-600">Statut:</span>{' '}
            <span className="font-medium text-green-600">Active</span>
          </div>
          <div>
            <span className="text-gray-600">Type:</span>{' '}
            <span className="font-medium">{acte.recurrence.typeRecurrence}</span>
          </div>
          {acte.prochaineEcheanceDate && (
            <div>
              <span className="text-gray-600">Prochaine échéance:</span>{' '}
              <span className="font-medium">
                {new Date(acte.prochaineEcheanceDate).toLocaleDateString('fr-FR')}
              </span>
            </div>
          )}
          {acte.recurrence?.rappelRecurrent?.active && (
            <div>
              <span className="text-gray-600">Rappel récurrences:</span>{' '}
              <span className="font-medium text-green-600">
                {acte.recurrence.rappelRecurrent.joursAvant} jour{acte.recurrence.rappelRecurrent.joursAvant > 1 ? 's' : ''} avant
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400">Aucune récurrence configurée</div>
      )
    )}
  </div>
</div>
            </div>

            {/* Colonne droite */}
            <div className="space-y-4">
              {/* Description et notes */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Description et notes</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Description</div>
                    {isEditing ? (
                      <textarea
                        value={editedActe.description || ''}
                        onChange={(e) => setEditedActe({ ...editedActe, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Description de l'acte..."
                      />
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-3">
                        {acte.description || <span className="text-gray-400">Aucune description</span>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Notes</div>
                    {isEditing ? (
                      <textarea
                        value={editedActe.notes || ''}
                        onChange={(e) => setEditedActe({ ...editedActe, notes: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Notes complémentaires..."
                      />
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-3">
                        {acte.notes || <span className="text-gray-400">Aucune note</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Documents */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">
                  Documents ({editedActe.documents?.length || 0})
                </h3>
                <div className="space-y-2">
                  {(isEditing || (acte.statut === 'planifie' && acte.statut !== 'supprime')) && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                        className="hidden"
                        id="file-upload-detail"
                        disabled={uploadingFiles}
                      />
                      <label htmlFor="file-upload-detail" className="cursor-pointer text-center block">
                        <Upload className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          {uploadingFiles ? 'Upload en cours...' : 'Ajouter des documents'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          📸 Images → Cloudinary (optimisées)<br/>
                          📄 PDF → Firebase Storage
                        </p>
                      </label>
                    </div>
                  )}
                  
                  {editedActe.documents?.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 flex-1">
                        {doc.type === 'image' && doc.thumbnailUrl ? (
                          <img 
                            src={doc.thumbnailUrl} 
                            alt={doc.nom}
                            className="w-8 h-8 rounded object-cover"
                          />
                        ) : (
                          <FileText className="h-4 w-4 text-gray-500" />
                        )}
                        <div>
                          <div className="text-sm font-medium">{doc.nom}</div>
                          <div className="text-xs text-gray-500">
                            {(doc.taille / 1024).toFixed(0)} KB
                            {doc.service === 'cloudinary' && ' • ☁️ Cloudinary'}
                            {doc.service === 'firebase' && ' • 🔥 Firebase'}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Voir"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                        <a
                          href={doc.url}
                          download={doc.nom}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        {(isEditing || (acte.statut === 'planifie' && acte.statut !== 'supprime')) && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1 hover:bg-gray-200 rounded text-red-600"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Historique */}
              {acte.historique && acte.historique.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Historique</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {acte.historique.map((event, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium">
                          {new Date(event.date).toLocaleDateString('fr-FR')} à {' '}
                          {new Date(event.date).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="text-gray-600">
                          {event.action === 'creation' ? 'création' : event.action}: {event.details}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ActeDetailModal;